// NEW_FILE_JUSTIFIED: New domain-specific upload hook for knowledge base RAG feature. No existing hook handles KB file upload with SHA-256 dedup and Convex storage flow.
import { useCallback, useState } from 'react';
import { useAction, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need convex api
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need convex dataModel types
import { toast } from 'sonner';

// ─── Accepted file type maps ──────────────────────────────────────────────────

export const ACCEPTED_TYPES: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/msword': 'docx',
  'text/plain': 'txt',
  'text/markdown': 'md',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
};

export const ACCEPTED_EXTENSIONS: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.doc': 'application/msword',
  '.txt': 'text/plain',
  '.md': 'text/markdown',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
};

// ─── SHA-256 helper ───────────────────────────────────────────────────────────

async function computeFileSha256(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// ─── File type detection ──────────────────────────────────────────────────────

function detectMimeType(file: File): string {
  if (file.type && ACCEPTED_TYPES[file.type]) return file.type;
  const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase();
  return ACCEPTED_EXTENSIONS[ext] ?? file.type;
}

function isAcceptedFile(file: File): boolean {
  if (ACCEPTED_TYPES[file.type]) return true;
  const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase();
  return ext in ACCEPTED_EXTENSIONS;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useKnowledgeBaseUpload() {
  const [isUploading, setIsUploading] = useState(false);

  const createUploadUrl = useMutation(api.storage.createUploadUrl);
  const createFileEntry = useMutation(api.knowledgeBase.mutations.createFileEntry);
  const processFile = useAction(api.knowledgeBase.processing.processFile);

  const uploadFiles = useCallback(
    async (
      files: File[],
      orgId: Id<'organisations'>,
      isSharedWithCompanies: boolean
    ): Promise<void> => {
      if (files.length === 0) return;

      const invalid = files.filter(f => !isAcceptedFile(f));
      if (invalid.length > 0) {
        const names = invalid.map(f => f.name).join(', ');
        toast.error(`Unsupported file type(s): ${names}`);
        return;
      }

      setIsUploading(true);

      const results = await Promise.allSettled(
        files.map(async file => {
          const contentHash = await computeFileSha256(file);
          const mimeType = detectMimeType(file);
          const extension = (file.name.split('.').pop() ?? '').toLowerCase();

          // Generate signed upload URL and upload to Convex storage
          const uploadUrl = await createUploadUrl();
          const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'Content-Type': mimeType },
            body: file,
          });

          if (!response.ok) {
            throw new Error(`Storage upload failed for "${file.name}" (${response.status})`);
          }

          const responseData = (await response.json()) as { storageId: string }; // AS_JUSTIFIED: Convex storage endpoint always returns { storageId: string }
          const fileStorageId = responseData.storageId as Id<'_storage'>; // AS_JUSTIFIED: one-time Id cast from Convex storage upload response

          // Create the document entry — server enforces dedup via contentHash
          const documentId = await createFileEntry({
            orgId,
            title: file.name,
            originalContent: '',
            contentHash,
            fileStorageId,
            fileName: file.name,
            fileType: extension || mimeType,
            isSharedWithCompanies,
          });

          // Fire-and-forget: trigger text extraction + RAG ingestion
          void processFile({
            documentId,
            orgId,
            fileStorageId,
            fileType: extension || mimeType,
            title: file.name,
            isSharedWithCompanies,
          });

          return { file, documentId };
        })
      );

      setIsUploading(false);

      const succeeded = results.filter(r => r.status === 'fulfilled');
      const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      const duplicates = failed.filter(
        r => r.reason instanceof Error && r.reason.message.includes('identical content')
      );
      const errors = failed.filter(
        r => !(r.reason instanceof Error && r.reason.message.includes('identical content'))
      );

      if (succeeded.length > 0) {
        toast.success(
          `${succeeded.length} file${succeeded.length > 1 ? 's' : ''} uploaded — processing will begin shortly`
        );
      }

      if (duplicates.length > 0) {
        toast.warning(
          `${duplicates.length} duplicate${duplicates.length > 1 ? 's' : ''} skipped — identical content already exists`
        );
      }

      if (errors.length > 0) {
        const firstError = errors[0];
        const message =
          firstError?.reason instanceof Error
            ? firstError.reason.message
            : 'Upload failed for one or more files';
        toast.error(message);
      }
    },
    [createUploadUrl, createFileEntry, processFile]
  );

  return { uploadFiles, isUploading };
}
